<script setup lang="ts">
export type CheckoutStep = 'contact' | 'payment' | 'summary'

const props = defineProps<{
    currentStep: CheckoutStep
}>()

const emit = defineEmits<{
    select: [step: CheckoutStep]
}>()

const steps = [
    { key: 'contact', labelKey: 'checkout.breadcrumb.contact' },
    { key: 'payment', labelKey: 'checkout.breadcrumb.payment' },
    { key: 'summary', labelKey: 'checkout.breadcrumb.summary' },
] as const satisfies ReadonlyArray<{ key: CheckoutStep, labelKey: string }>

const currentIndex = computed(() => steps.findIndex((step) => step.key === props.currentStep))

function badgeClass(index: number) {
    if (index < currentIndex.value) return 'bg-base-content/55 text-base-100'
    if (index === currentIndex.value) return 'bg-base-content text-base-100 font-semibold'
    return 'border border-base-content/55 text-base-content/55'
}

// A segment is dashed until the step it leads to has been reached.
function lineClass(segment: number) {
    return segment > currentIndex.value ? 'border-dashed' : ''
}
</script>

<template>
    <nav
        class="flex items-start"
        data-cy="checkout-step-indicator"
        :aria-label="$t('checkout.breadcrumb.label')"
    >
        <template v-for="(step, index) in steps" :key="step.key">
            <!--
                Each step's own column is as wide as its label, not a shared fixed width,
                so the first/last label ends flush with the nav instead of overflowing
                past it. The badge sits centred in that column and shifts with it, so on
                long labels the dashed line lands in the column's side padding rather than
                touching the badge - an accepted trade-off for flush outer edges.
            -->
            <div
                v-if="index > 0"
                class="mt-[11px] flex-1 border-t border-base-content/55"
                :class="lineClass(index)"
                aria-hidden="true"
            />
            <button
                type="button"
                class="relative flex shrink-0 flex-col items-center gap-1 cursor-pointer"
                :class="step.key"
                :aria-current="index === currentIndex ? 'step' : undefined"
                @click="emit('select', step.key)"
            >
                <span
                    class="flex size-[22px] shrink-0 items-center justify-center rounded-full text-xs leading-none"
                    :class="badgeClass(index)"
                >
                    <MiscIcon v-if="index < currentIndex" name="check" size="xs" />
                    <template v-else>{{ index + 1 }}</template>
                </span>
                <span
                    class="whitespace-nowrap text-xs"
                    :class="index === currentIndex ? 'font-semibold text-base-content' : 'text-base-content/55'"
                >
                    {{ $t(step.labelKey) }}
                </span>
            </button>
        </template>
    </nav>
</template>
